import { Json } from "@redotech/json/json";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Message Parts
export interface TextPart extends z.infer<typeof TextPartSchema_> {}
export const TextPartSchema_ = z.object({
  type: z.literal("text"),
  text: z.string(),
});
export const TextPartSchema: z.ZodType<TextPart> = TextPartSchema_;

export interface ImagePart extends z.infer<typeof ImagePartSchema_> {}
export const ImagePartSchema_ = z.object({
  type: z.literal("image"),
  image: z.url(),
  providerOptions: z
    .record(z.string(), z.record(z.string(), z.json()))
    .optional(),
});
export const ImagePartSchema: z.ZodType<ImagePart> = ImagePartSchema_;

export interface FilePart extends z.infer<typeof FilePartSchema_> {}
export const FilePartSchema_ = z.object({
  type: z.literal("file"),
  data: z.url(),
  mediaType: z.string(),
  /** Original filename, when the source (e.g. a chat attachment) knows it.
   *  Lets consumers reference the file by name instead of parsing the URL. */
  filename: z.string().optional(),
});
export const FilePartSchema: z.ZodType<FilePart> = FilePartSchema_;

export interface ToolCallPart extends z.infer<typeof ToolCallPartSchema_> {}
export const ToolCallPartSchema_ = z.object({
  type: z.literal("tool-call"),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.json().meta({ annotation: "JSON" }),
});
export const ToolCallPartSchema: z.ZodType<ToolCallPart> = ToolCallPartSchema_;

export const TextToolResultOutputSchema_ = z.object({
  type: z.literal("text"),
  value: z.string(),
});
export interface TextToolResultOutput extends z.infer<
  typeof TextToolResultOutputSchema_
> {}

export const JsonToolResultOutputSchema_ = z.object({
  type: z.literal("json"),
  value: z.json(),
});
export interface JsonToolResultOutput extends z.infer<
  typeof JsonToolResultOutputSchema_
> {}

export const ErrorTextToolResultOutputSchema_ = z.object({
  type: z.literal("error-text"),
  value: z.string(),
});
export interface ErrorTextToolResultOutput extends z.infer<
  typeof ErrorTextToolResultOutputSchema_
> {}

export const ErrorJsonToolResultOutputSchema_ = z.object({
  type: z.literal("error-json"),
  value: z.json(),
});
export interface ErrorJsonToolResultOutput extends z.infer<
  typeof ErrorJsonToolResultOutputSchema_
> {}

export const ToolResultOutputSchema = z.union([
  TextToolResultOutputSchema_,
  JsonToolResultOutputSchema_,
  ErrorTextToolResultOutputSchema_,
  ErrorJsonToolResultOutputSchema_,
]);
export type ToolResultOutput = z.infer<typeof ToolResultOutputSchema>;

/**
 * Extracts the raw value from a structured ToolResultOutput.
 * Use this when you need the underlying value regardless of output type.
 * Returns string | JSONType (which allows property access without casts).
 */
export function extractOutputValue(
  output: ToolResultOutput,
): ToolResultOutput["value"] {
  return output.value;
}

export function createToolResultOutput(
  value: Json,
  isError?: boolean,
): ToolResultOutput {
  if (isError) {
    return typeof value === "string"
      ? { type: "error-text", value }
      : { type: "error-json", value };
  }
  if (typeof value === "string") {
    return { type: "text", value };
  }
  return { type: "json", value };
}

export interface ToolResultPart extends z.infer<typeof ToolResultPartSchema_> {}
export const ToolResultPartSchema_ = z.object({
  type: z.literal("tool-result"),
  toolCallId: z.string(),
  toolName: z.string(),
  output: ToolResultOutputSchema,
  isError: z.boolean().nullish(),
  errorMetadata: z.record(z.string(), z.json()).nullish(),
});
export const ToolResultPartSchema: z.ZodType<ToolResultPart> =
  ToolResultPartSchema_;

export interface ReasoningPart extends z.infer<typeof ReasoningPartSchema_> {}
export const ReasoningPartSchema_ = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
  signature: z.string().optional(),
});
export const ReasoningPartSchema: z.ZodType<ReasoningPart> =
  ReasoningPartSchema_;

export const UserMessageContentSchema = z.union([
  TextPartSchema,
  ImagePartSchema,
  FilePartSchema,
]);
export type UserMessageContent = z.infer<typeof UserMessageContentSchema>;

export const AssistantMessageContentSchema = z.union([
  TextPartSchema,
  ToolCallPartSchema,
  ReasoningPartSchema,
]);
export type AssistantMessageContent = z.infer<
  typeof AssistantMessageContentSchema
>;
// End Message Parts

// Messages
export const SystemMessageSchema_ = z.object({
  id: z.string().nullish(),
  externalMessageId: z.string().nullish(),
  role: z.literal("system"),
  content: z.string(),
});
export interface SystemMessage extends z.infer<typeof SystemMessageSchema_> {}
export const SystemMessageSchema: z.ZodType<SystemMessage> =
  SystemMessageSchema_;

export const UserMessageSchema_ = z.object({
  id: z.string().nullish(),
  externalMessageId: z.string().nullish(),
  role: z.literal("user"),
  content: z.union([z.string(), z.array(UserMessageContentSchema)]),
});
export interface UserMessage extends z.infer<typeof UserMessageSchema_> {}
export const UserMessageSchema: z.ZodType<UserMessage> = UserMessageSchema_;

export const AiProviderMetricsSchema = z.object({
  inputTokens: z.number().nullish(),
  outputTokens: z.number().nullish(),
  providerRequestTimeMs: z.number().nullish(),
  cacheReadInputTokens: z.number().nullish(),
  cacheWriteInputTokens: z.number().nullish(),
  reasoningOutputTokens: z.number().nullish(),
  model: z.string().nullish(),
  provider: z.string().nullish(),
  group: z.string().nullish(),
});
export type AiProviderMetrics = z.infer<typeof AiProviderMetricsSchema>;

export const AssistantMessageSchema_ = z.object({
  id: z.string().nullish(),
  externalMessageId: z.string().nullish(),
  role: z.literal("assistant"),
  content: z.union([z.string(), z.array(AssistantMessageContentSchema)]),
  feedback: createEnum(["positive", "negative"], {
    enumName: "LlmAssistantMessageFeedback",
  }).nullish(),
  comment: z.string().nullish(),
  usage: AiProviderMetricsSchema.nullish(),
});
export interface AssistantMessage extends z.infer<
  typeof AssistantMessageSchema_
> {}
export const AssistantMessageSchema: z.ZodType<AssistantMessage> =
  AssistantMessageSchema_;

export const ToolMessageSchema_ = z.object({
  id: z.string().nullish(),
  externalMessageId: z.string().nullish(),
  role: z.literal("tool"),
  content: z.array(ToolResultPartSchema),
});
export interface ToolMessage extends z.infer<typeof ToolMessageSchema_> {}
export const ToolMessageSchema: z.ZodType<ToolMessage> = ToolMessageSchema_;

export const MessageSchema_ = z.union([
  SystemMessageSchema,
  UserMessageSchema,
  AssistantMessageSchema,
  ToolMessageSchema,
]);
export type Message = z.infer<typeof MessageSchema_>;
export const MessageSchema: z.ZodType<Message> = MessageSchema_;

export type MessageContent =
  | ToolResultPart
  | AssistantMessageContent
  | FilePart
  | ImagePart;

export function extractJsonToolResultValues(
  messages: readonly Message[],
  toolNames: string | ReadonlySet<string>,
): Json[] {
  const includesToolName = (toolName: string) =>
    typeof toolNames === "string"
      ? toolName === toolNames
      : toolNames.has(toolName);

  return messages.flatMap((message) =>
    message.role !== "tool"
      ? []
      : message.content.flatMap((part) =>
          part.type === "tool-result" &&
          includesToolName(part.toolName) &&
          part.output.type === "json"
            ? [part.output.value]
            : [],
        ),
  );
}

export const MessageRoleSchema = createEnum(
  ["system", "user", "assistant", "tool"],
  { enumName: "MessageRole" },
);
export type MessageRole = z.infer<typeof MessageRoleSchema>;
